/**
 * Compress an image File to under maxKB using Canvas.
 * Outputs WebP (best compression), falls back to JPEG.
 * Scales down dimensions if the image is very large.
 */
export async function compressImage(file, maxKB = 100) {
  const maxBytes = maxKB * 1024;

  // If already small enough, return as-is
  if (file.size <= maxBytes) return file;

  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');

  // Scale down if either dimension > 1920px
  const MAX_DIM = 1920;
  let { width, height } = bitmap;
  if (width > MAX_DIM || height > MAX_DIM) {
    const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // Try WebP first, then JPEG — step quality down until under maxBytes
  const formats = ['image/webp', 'image/jpeg'];
  for (const format of formats) {
    for (let quality = 0.85; quality >= 0.3; quality -= 0.1) {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, format, quality));
      if (!blob) continue;
      if (blob.size <= maxBytes || quality <= 0.3) {
        const ext = format === 'image/webp' ? 'webp' : 'jpg';
        const name = file.name.replace(/\.[^.]+$/, `.${ext}`);
        return new File([blob], name, { type: format });
      }
    }
  }

  // Worst case: return last attempt
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.3));
  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
}
